// src/providers/dialog-provider.tsx
'use client';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import React from 'react';
import { createContext, useContext, useState } from 'react';

type AlertDialogOptions = {
  title: string | React.ReactNode;
  description?: string | React.ReactNode;
  content?: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmClassName?: string;
  cancelClassName?: string;
  containerClassName?: string;
  titleClassName?: string;
  contentClassName?: string;
  footerClassName?: string;
  showCancelButton?: boolean;
  ConfirmButton?: React.JSX.Element;
  size?: 'lg' | 'default' | 'fit';
};

type DialogContextType = {
  alert: (options: AlertDialogOptions) => Promise<boolean>;
};

const DialogContext = createContext<DialogContextType | null>(null);

export function DialogProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [dialogOptions, setDialogOptions] = useState<AlertDialogOptions | null>(null);
  const [resolvePromise, setResolvePromise] = useState<(value: boolean) => void>();

  const alert = (options: AlertDialogOptions): Promise<boolean> => {
    setDialogOptions(options);
    setOpen(true);

    return new Promise((resolve) => {
      setResolvePromise(() => resolve);
    });
  };

  const handleConfirm = () => {
    resolvePromise?.(true);
    setOpen(false);
  };

  const handleCancel = () => {
    resolvePromise?.(false);
    setOpen(false);
  };
  const ConfirmButton = dialogOptions?.ConfirmButton;
  const size = dialogOptions?.size || 'default';
  return (
    <DialogContext.Provider value={{ alert }}>
      {children}
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent
          className={cn(
            size === 'default' && "max-w-[400px] p-8",
            size === 'lg' && "max-w-[450px] p-10",
            size === 'fit' && "max-w-fit p-10",
            "max-h-screen flex flex-col",
            dialogOptions?.containerClassName,
          )}
        >
          {dialogOptions && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle className={cn(
                  size === 'default' && 'text-left text-lg font-medium',
                  (size === 'lg' || size === 'fit') && 'text-center text-3xl',
                  dialogOptions.titleClassName,
                )}>
                  {dialogOptions.title}
                </AlertDialogTitle>
                {
                  <AlertDialogDescription hidden={!!dialogOptions.description} className="text-base">
                    {dialogOptions.description}
                  </AlertDialogDescription>
                }
              </AlertDialogHeader>
              <div className={cn(
                "flex-1 overflow-auto",
                size === 'default' && 'text-sm p-0',
                size === 'lg' && 'text-xl p-5',
              )}>
                {
                  dialogOptions.content
                }
              </div>
              <AlertDialogFooter className={cn(
                'flex flex-row items-center justify-center sm:items-center sm:justify-center',
                size === 'default' && 'gap-7',
                size === 'lg' && 'gap-7',
                dialogOptions.footerClassName || ''
              )}>
                {
                  (dialogOptions.showCancelButton ?? true) ? (
                    <AlertDialogCancel
                      className={
                        cn(
                          "min-w-[120px] max-w-1/2 flex-1",
                          dialogOptions.cancelClassName,
                        )
                      }
                      onClick={handleCancel}
                    >
                      {dialogOptions.cancelText || '取消'}
                    </AlertDialogCancel>
                  ) : null
                }
                {ConfirmButton ? (
                  React.cloneElement(ConfirmButton, {
                    className: "min-w-[120px] max-w-1/2 flex-1",
                    onClick: handleConfirm
                  })
                ) : (
                  <AlertDialogAction className={
                    cn(
                      "min-w-[120px] max-w-1/2",
                      dialogOptions.showCancelButton ? 'flex-1' : 'w-1/2',
                      dialogOptions.confirmClassName,
                    )
                  } onClick={handleConfirm}>
                    {dialogOptions.confirmText || '确认'}
                  </AlertDialogAction>
                )}
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </DialogContext.Provider >
  );
}

export function useDialog() {
  const context = useContext(DialogContext);

  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }

  return context;
}